import type { DisputeType, Role, User } from '@/types/dispute-types';

export const mockRoles: Role[] = [
  { id: 'role-1', name: 'Dispute Manager', description: 'Manages all dispute operations' },
  { id: 'role-2', name: 'Senior Analyst', description: 'Senior dispute analyst' },
  { id: 'role-3', name: 'Analyst', description: 'Dispute analyst' },
  { id: 'role-4', name: 'Legal Team', description: 'Legal department' },
  { id: 'role-5', name: 'Finance Manager', description: 'Finance department manager' },
  { id: 'role-6', name: 'Customer Success', description: 'Customer success team' },
];

export const mockUsers: User[] = [
  { id: 'user-1', name: 'John Smith', email: 'john.smith@company.com', role: 'Dispute Manager' },
  { id: 'user-2', name: 'Sarah Johnson', email: 'sarah.johnson@company.com', role: 'Senior Analyst' },
  { id: 'user-3', name: 'Mike Chen', email: 'mike.chen@company.com', role: 'Analyst' },
  { id: 'user-4', name: 'Emily Davis', email: 'emily.davis@company.com', role: 'Analyst' },
  { id: 'user-5', name: 'David Wilson', email: 'david.wilson@company.com', role: 'Legal Team' },
];

export function generateMockDisputeTypes(): DisputeType[] {
  return [
    {
      id: 'dt-1',
      name: 'Billing Error',
      description: 'Disputes related to incorrect billing amounts, pricing discrepancies, or calculation errors',
      isActive: true,
      createdAt: '2024-01-10T10:00:00Z',
      updatedAt: '2024-01-15T14:30:00Z',
      createdBy: 'John Smith',
      usageCount: 45,
      avgResolutionTime: 8,
      successRate: 92,
      workflow: {
        id: 'wf-1',
        disputeTypeId: 'dt-1',
        stages: [
          {
            id: 'stage-1',
            name: 'Submitted',
            description: 'Initial dispute submission and validation',
            order: 1,
            color: '#3B82F6',
            icon: 'FileText',
            properties: {
              requiresApproval: false,
              slaHours: 24,
              slaWarningHours: 20,
              autoEscalate: true,
              escalationRoles: ['Dispute Manager'],
              allowReassignment: true,
              allowedAssigneeRoles: ['Analyst', 'Senior Analyst'],
            },
            notifications: [
              {
                id: 'notif-1',
                event: 'stage_entry',
                recipients: [{ type: 'assignee' }],
                template: 'dispute_submitted',
                channels: ['email', 'in_app'],
              },
            ],
          },
          {
            id: 'stage-2',
            name: 'Under Investigation',
            description: 'Gathering evidence and analysing the dispute',
            order: 2,
            color: '#8B5CF6',
            icon: 'Search',
            properties: {
              requiresApproval: false,
              slaHours: 72,
              slaWarningHours: 60,
              autoEscalate: true,
              escalationRoles: ['Senior Analyst'],
              requiredDocuments: ['invoice', 'contract'],
              allowReassignment: true,
              allowedAssigneeRoles: ['Analyst', 'Senior Analyst'],
            },
            notifications: [
              {
                id: 'notif-2',
                event: 'sla_warning',
                recipients: [{ type: 'assignee' }, { type: 'role', value: 'Dispute Manager' }],
                template: 'sla_warning',
                channels: ['email', 'in_app'],
              },
            ],
          },
          {
            id: 'stage-3',
            name: 'Resolution Proposed',
            description: 'Resolution has been proposed and awaiting approval',
            order: 3,
            color: '#F59E0B',
            icon: 'CheckCircle',
            properties: {
              requiresApproval: true,
              approverRoles: ['Senior Analyst', 'Dispute Manager'],
              slaHours: 48,
              slaWarningHours: 40,
              autoEscalate: true,
              escalationRoles: ['Dispute Manager'],
              allowReassignment: false,
            },
            notifications: [
              {
                id: 'notif-3',
                event: 'approval_required',
                recipients: [{ type: 'role', value: 'Senior Analyst' }],
                template: 'approval_required',
                channels: ['email', 'in_app'],
              },
            ],
          },
          {
            id: 'stage-4',
            name: 'Resolved',
            description: 'Dispute has been resolved and closed',
            order: 4,
            color: '#10B981',
            icon: 'CheckCircle2',
            properties: {
              requiresApproval: false,
              allowReassignment: false,
              autoEscalate: false,
            },
            notifications: [
              {
                id: 'notif-4',
                event: 'stage_entry',
                recipients: [{ type: 'creator' }],
                template: 'dispute_resolved',
                channels: ['email'],
              },
            ],
          },
        ],
        transitions: [
          {
            id: 'trans-1',
            fromStageId: 'stage-1',
            toStageId: 'stage-2',
            name: 'Begin Investigation',
            requiresComment: true,
            requiresApproval: false,
            autoTransition: false,
          },
          {
            id: 'trans-2',
            fromStageId: 'stage-2',
            toStageId: 'stage-3',
            name: 'Propose Resolution',
            requiresComment: true,
            requiresApproval: false,
            autoTransition: false,
          },
          {
            id: 'trans-3',
            fromStageId: 'stage-3',
            toStageId: 'stage-4',
            name: 'Approve & Close',
            requiresComment: false,
            requiresApproval: true,
            approverRoles: ['Senior Analyst', 'Dispute Manager'],
            autoTransition: false,
          },
          {
            id: 'trans-4',
            fromStageId: 'stage-3',
            toStageId: 'stage-2',
            name: 'Request More Information',
            requiresComment: true,
            requiresApproval: false,
            autoTransition: false,
          },
        ],
      },
    },
    {
      id: 'dt-2',
      name: 'Service Quality',
      description: 'Disputes regarding service delivery quality, performance issues, or unmet expectations',
      isActive: true,
      createdAt: '2024-01-08T09:00:00Z',
      updatedAt: '2024-01-12T16:45:00Z',
      createdBy: 'Sarah Johnson',
      usageCount: 28,
      avgResolutionTime: 12,
      successRate: 85,
      workflow: {
        id: 'wf-2',
        disputeTypeId: 'dt-2',
        stages: [
          {
            id: 'stage-5',
            name: 'Initial Review',
            description: 'First review of service quality complaint',
            order: 1,
            color: '#3B82F6',
            icon: 'FileText',
            properties: {
              requiresApproval: false,
              slaHours: 48,
              slaWarningHours: 40,
              autoEscalate: true,
              escalationRoles: ['Customer Success'],
              allowReassignment: true,
              allowedAssigneeRoles: ['Customer Success', 'Analyst'],
            },
            notifications: [
              {
                id: 'notif-5',
                event: 'stage_entry',
                recipients: [{ type: 'assignee' }],
                template: 'quality_dispute_received',
                channels: ['email', 'in_app'],
              },
            ],
          },
          {
            id: 'stage-6',
            name: 'Technical Assessment',
            description: 'Technical team evaluates the service quality issues',
            order: 2,
            color: '#8B5CF6',
            icon: 'Settings',
            properties: {
              requiresApproval: false,
              slaHours: 96,
              slaWarningHours: 80,
              autoEscalate: true,
              escalationRoles: ['Senior Analyst'],
              allowReassignment: true,
              allowedAssigneeRoles: ['Analyst', 'Senior Analyst'],
            },
            notifications: [],
          },
          {
            id: 'stage-7',
            name: 'Management Review',
            description: 'Senior management reviews and approves resolution',
            order: 3,
            color: '#F59E0B',
            icon: 'Users',
            properties: {
              requiresApproval: true,
              approverRoles: ['Dispute Manager', 'Finance Manager'],
              slaHours: 72,
              slaWarningHours: 60,
              autoEscalate: false,
              allowReassignment: false,
            },
            notifications: [
              {
                id: 'notif-6',
                event: 'approval_required',
                recipients: [{ type: 'role', value: 'Dispute Manager' }],
                template: 'management_review_required',
                channels: ['email', 'in_app'],
              },
            ],
          },
          {
            id: 'stage-8',
            name: 'Closed',
            description: 'Dispute closed with resolution',
            order: 4,
            color: '#10B981',
            icon: 'CheckCircle2',
            properties: {
              requiresApproval: false,
              allowReassignment: false,
              autoEscalate: false,
            },
            notifications: [
              {
                id: 'notif-7',
                event: 'stage_entry',
                recipients: [{ type: 'creator' }],
                template: 'dispute_closed',
                channels: ['email'],
              },
            ],
          },
        ],
        transitions: [
          {
            id: 'trans-5',
            fromStageId: 'stage-5',
            toStageId: 'stage-6',
            name: 'Send for Assessment',
            requiresComment: true,
            requiresApproval: false,
            autoTransition: false,
          },
          {
            id: 'trans-6',
            fromStageId: 'stage-6',
            toStageId: 'stage-7',
            name: 'Submit for Review',
            requiresComment: true,
            requiresApproval: false,
            autoTransition: false,
          },
          {
            id: 'trans-7',
            fromStageId: 'stage-7',
            toStageId: 'stage-8',
            name: 'Approve & Close',
            requiresComment: false,
            requiresApproval: true,
            approverRoles: ['Dispute Manager'],
            autoTransition: false,
          },
        ],
      },
    },
    {
      id: 'dt-3',
      name: 'Payment Dispute',
      description: 'Disputes related to payment processing, allocation errors, or payment not received',
      isActive: true,
      createdAt: '2024-01-05T11:30:00Z',
      updatedAt: '2024-01-18T09:15:00Z',
      createdBy: 'Mike Chen',
      usageCount: 62,
      avgResolutionTime: 5,
      successRate: 95,
      workflow: {
        id: 'wf-3',
        disputeTypeId: 'dt-3',
        stages: [
          {
            id: 'stage-9',
            name: 'Payment Verification',
            description: 'Verify payment records and transaction history',
            order: 1,
            color: '#3B82F6',
            icon: 'DollarSign',
            properties: {
              requiresApproval: false,
              slaHours: 24,
              slaWarningHours: 20,
              autoEscalate: true,
              escalationRoles: ['Finance Manager'],
              allowReassignment: true,
              allowedAssigneeRoles: ['Analyst'],
            },
            notifications: [
              {
                id: 'notif-8',
                event: 'stage_entry',
                recipients: [{ type: 'assignee' }],
                template: 'payment_dispute_received',
                channels: ['email', 'in_app'],
              },
            ],
          },
          {
            id: 'stage-10',
            name: 'Reconciliation',
            description: 'Reconcile payment records with bank statements',
            order: 2,
            color: '#8B5CF6',
            icon: 'Calculator',
            properties: {
              requiresApproval: false,
              slaHours: 48,
              slaWarningHours: 40,
              autoEscalate: true,
              escalationRoles: ['Finance Manager'],
              allowReassignment: true,
              allowedAssigneeRoles: ['Analyst', 'Finance Manager'],
            },
            notifications: [],
          },
          {
            id: 'stage-11',
            name: 'Resolution',
            description: 'Apply corrections and close dispute',
            order: 3,
            color: '#10B981',
            icon: 'CheckCircle2',
            properties: {
              requiresApproval: true,
              approverRoles: ['Finance Manager'],
              allowReassignment: false,
              autoEscalate: false,
            },
            notifications: [
              {
                id: 'notif-9',
                event: 'stage_entry',
                recipients: [{ type: 'creator' }],
                template: 'payment_dispute_resolved',
                channels: ['email'],
              },
            ],
          },
        ],
        transitions: [
          {
            id: 'trans-8',
            fromStageId: 'stage-9',
            toStageId: 'stage-10',
            name: 'Begin Reconciliation',
            requiresComment: true,
            requiresApproval: false,
            autoTransition: false,
          },
          {
            id: 'trans-9',
            fromStageId: 'stage-10',
            toStageId: 'stage-11',
            name: 'Apply Resolution',
            requiresComment: true,
            requiresApproval: true,
            approverRoles: ['Finance Manager'],
            autoTransition: false,
          },
        ],
      },
    },
    {
      id: 'dt-4',
      name: 'Contract Terms',
      description: 'Disputes involving contract interpretation, terms disagreement, or pricing structure',
      isActive: true,
      createdAt: '2024-01-03T14:00:00Z',
      updatedAt: '2024-01-10T11:20:00Z',
      createdBy: 'Emily Davis',
      usageCount: 15,
      avgResolutionTime: 18,
      successRate: 78,
      workflow: {
        id: 'wf-4',
        disputeTypeId: 'dt-4',
        stages: [
          {
            id: 'stage-12',
            name: 'Initial Assessment',
            description: 'Review contract and identify disputed terms',
            order: 1,
            color: '#3B82F6',
            icon: 'FileText',
            properties: {
              requiresApproval: false,
              slaHours: 48,
              slaWarningHours: 40,
              autoEscalate: true,
              escalationRoles: ['Legal Team'],
              requiredDocuments: ['contract', 'amendment'],
              allowReassignment: true,
              allowedAssigneeRoles: ['Legal Team', 'Senior Analyst'],
            },
            notifications: [
              {
                id: 'notif-10',
                event: 'stage_entry',
                recipients: [{ type: 'role', value: 'Legal Team' }],
                template: 'contract_dispute_received',
                channels: ['email', 'in_app'],
              },
            ],
          },
          {
            id: 'stage-13',
            name: 'Legal Review',
            description: 'Legal team reviews contract terms and provides interpretation',
            order: 2,
            color: '#8B5CF6',
            icon: 'Scale',
            properties: {
              requiresApproval: false,
              slaHours: 120,
              slaWarningHours: 96,
              autoEscalate: true,
              escalationRoles: ['Dispute Manager'],
              allowReassignment: true,
              allowedAssigneeRoles: ['Legal Team'],
            },
            notifications: [],
          },
          {
            id: 'stage-14',
            name: 'Negotiation',
            description: 'Negotiate resolution with customer',
            order: 3,
            color: '#F59E0B',
            icon: 'MessageSquare',
            properties: {
              requiresApproval: false,
              slaHours: 96,
              slaWarningHours: 80,
              autoEscalate: false,
              allowReassignment: true,
              allowedAssigneeRoles: ['Senior Analyst', 'Dispute Manager'],
            },
            notifications: [],
          },
          {
            id: 'stage-15',
            name: 'Final Approval',
            description: 'Management approves negotiated resolution',
            order: 4,
            color: '#F59E0B',
            icon: 'CheckCircle',
            properties: {
              requiresApproval: true,
              approverRoles: ['Dispute Manager', 'Finance Manager'],
              slaHours: 48,
              slaWarningHours: 40,
              autoEscalate: false,
              allowReassignment: false,
            },
            notifications: [
              {
                id: 'notif-11',
                event: 'approval_required',
                recipients: [{ type: 'role', value: 'Dispute Manager' }],
                template: 'final_approval_required',
                channels: ['email', 'in_app'],
              },
            ],
          },
          {
            id: 'stage-16',
            name: 'Closed',
            description: 'Dispute resolved and closed',
            order: 5,
            color: '#10B981',
            icon: 'CheckCircle2',
            properties: {
              requiresApproval: false,
              allowReassignment: false,
              autoEscalate: false,
            },
            notifications: [
              {
                id: 'notif-12',
                event: 'stage_entry',
                recipients: [{ type: 'creator' }],
                template: 'contract_dispute_resolved',
                channels: ['email'],
              },
            ],
          },
        ],
        transitions: [
          {
            id: 'trans-10',
            fromStageId: 'stage-12',
            toStageId: 'stage-13',
            name: 'Send to Legal',
            requiresComment: true,
            requiresApproval: false,
            autoTransition: false,
          },
          {
            id: 'trans-11',
            fromStageId: 'stage-13',
            toStageId: 'stage-14',
            name: 'Begin Negotiation',
            requiresComment: true,
            requiresApproval: false,
            autoTransition: false,
          },
          {
            id: 'trans-12',
            fromStageId: 'stage-14',
            toStageId: 'stage-15',
            name: 'Submit for Approval',
            requiresComment: true,
            requiresApproval: false,
            autoTransition: false,
          },
          {
            id: 'trans-13',
            fromStageId: 'stage-15',
            toStageId: 'stage-16',
            name: 'Approve & Close',
            requiresComment: false,
            requiresApproval: true,
            approverRoles: ['Dispute Manager'],
            autoTransition: false,
          },
          {
            id: 'trans-14',
            fromStageId: 'stage-14',
            toStageId: 'stage-13',
            name: 'Request Legal Clarification',
            requiresComment: true,
            requiresApproval: false,
            autoTransition: false,
          },
        ],
      },
    },
    {
      id: 'dt-5',
      name: 'Duplicate Charge',
      description: 'Disputes for duplicate invoices or charges',
      isActive: true,
      createdAt: '2024-01-12T08:30:00Z',
      updatedAt: '2024-01-12T08:30:00Z',
      createdBy: 'John Smith',
      usageCount: 8,
      avgResolutionTime: 3,
      successRate: 98,
      workflow: {
        id: 'wf-5',
        disputeTypeId: 'dt-5',
        stages: [
          {
            id: 'stage-17',
            name: 'Verification',
            description: 'Verify duplicate charge claim',
            order: 1,
            color: '#3B82F6',
            icon: 'Search',
            properties: {
              requiresApproval: false,
              slaHours: 12,
              slaWarningHours: 10,
              autoEscalate: true,
              escalationRoles: ['Senior Analyst'],
              allowReassignment: true,
              allowedAssigneeRoles: ['Analyst'],
            },
            notifications: [
              {
                id: 'notif-13',
                event: 'stage_entry',
                recipients: [{ type: 'assignee' }],
                template: 'duplicate_charge_received',
                channels: ['email', 'in_app'],
              },
            ],
          },
          {
            id: 'stage-18',
            name: 'Credit Issued',
            description: 'Credit memo issued and dispute closed',
            order: 2,
            color: '#10B981',
            icon: 'CheckCircle2',
            properties: {
              requiresApproval: true,
              approverRoles: ['Senior Analyst', 'Finance Manager'],
              allowReassignment: false,
              autoEscalate: false,
            },
            notifications: [
              {
                id: 'notif-14',
                event: 'stage_entry',
                recipients: [{ type: 'creator' }],
                template: 'credit_issued',
                channels: ['email'],
              },
            ],
          },
        ],
        transitions: [
          {
            id: 'trans-15',
            fromStageId: 'stage-17',
            toStageId: 'stage-18',
            name: 'Issue Credit',
            requiresComment: true,
            requiresApproval: true,
            approverRoles: ['Senior Analyst', 'Finance Manager'],
            autoTransition: false,
          },
        ],
      },
    },
    {
      id: 'dt-6',
      name: 'Product Defect',
      description: 'Disputes related to defective products or goods not as described',
      isActive: false,
      createdAt: '2023-12-15T10:00:00Z',
      updatedAt: '2024-01-05T15:30:00Z',
      createdBy: 'Sarah Johnson',
      usageCount: 3,
      avgResolutionTime: 15,
      successRate: 67,
      workflow: {
        id: 'wf-6',
        disputeTypeId: 'dt-6',
        stages: [
          {
            id: 'stage-19',
            name: 'Claim Received',
            description: 'Product defect claim received',
            order: 1,
            color: '#3B82F6',
            icon: 'AlertCircle',
            properties: {
              requiresApproval: false,
              slaHours: 48,
              slaWarningHours: 40,
              autoEscalate: true,
              escalationRoles: ['Customer Success'],
              allowReassignment: true,
              allowedAssigneeRoles: ['Customer Success'],
            },
            notifications: [],
          },
          {
            id: 'stage-20',
            name: 'Investigation',
            description: 'Investigate product defect claim',
            order: 2,
            color: '#8B5CF6',
            icon: 'Search',
            properties: {
              requiresApproval: false,
              slaHours: 96,
              slaWarningHours: 80,
              autoEscalate: true,
              escalationRoles: ['Senior Analyst'],
              allowReassignment: true,
              allowedAssigneeRoles: ['Analyst', 'Senior Analyst'],
            },
            notifications: [],
          },
          {
            id: 'stage-21',
            name: 'Resolved',
            description: 'Defect claim resolved',
            order: 3,
            color: '#10B981',
            icon: 'CheckCircle2',
            properties: {
              requiresApproval: true,
              approverRoles: ['Dispute Manager'],
              allowReassignment: false,
              autoEscalate: false,
            },
            notifications: [],
          },
        ],
        transitions: [
          {
            id: 'trans-16',
            fromStageId: 'stage-19',
            toStageId: 'stage-20',
            name: 'Begin Investigation',
            requiresComment: true,
            requiresApproval: false,
            autoTransition: false,
          },
          {
            id: 'trans-17',
            fromStageId: 'stage-20',
            toStageId: 'stage-21',
            name: 'Resolve Claim',
            requiresComment: true,
            requiresApproval: true,
            approverRoles: ['Dispute Manager'],
            autoTransition: false,
          },
        ],
      },
    },
  ];
}
